import { Box, Button, Stack, TextField, Typography, theme } from '@dtx-company/design-system/src'
import { FormHelperText, Link } from '@mui/material'
import { Plan } from '@dtx-company/true-common/src/types/Plan'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { useBuyPlanPageContext } from '../../context/useBuyPlanPageContext'
import { useForm } from 'react-hook-form'
import { useOnce } from '@dtx-company/true-common/src/hooks/useOnce'
import { usePlanPurchasePromoCode } from '@app/plans/src/PlanPurchaseForm/hooks/usePlanPurchasePromoCode'
import { usePlanPurchaseProviderContext } from '@app/plans/src/purchase/usePlanPurchaseProviderContext/usePlanPurchaseProviderContext'
import React, { useEffect, useState } from 'react'

interface FormFields {
  promoCode: string
}

export const BuyPlanPagePlanPromoCode: React.FC = () => {
  const { selectedPlan } = usePlanPurchaseProviderContext()
  const [showPromoCodeInput, setShowPromoCodeInput] = useState(false)
  const [value, setValue] = useState<string>('')
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [lastSelectedPlan, setLastSelectedPlan] = useState<Plan | undefined>(undefined)
  const [lastValue, setLastValue] = useState<string>('')
  const { document, validPromoCode, setValidPromoCode } = useBuyPlanPageContext()
  const { data } = document
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting, errors }
  } = useForm<FormFields>()

  const promoCodeState = usePlanPurchasePromoCode(lastValue, selectedPlan)

  useOnce(() => {
    if (!selectedPlan) return false
    setLastSelectedPlan(selectedPlan)
    if (validPromoCode) {
      setValue(validPromoCode)
      setLastValue(validPromoCode)
      setShowPromoCodeInput(true)
    }
  })

  useEffect(() => {
    // should submit only if promo code state and selected plan are loaded AND either the user
    // hit apply or the user has a promo code in the field but switches their plan interval
    const shouldSubmit =
      lastSelectedPlan &&
      (isSubmitted || (lastSelectedPlan !== selectedPlan && promoCodeState.code)) &&
      !promoCodeState.isLoading

    if (!shouldSubmit) return
    let error = ''

    if (promoCodeState.isValid && promoCodeState.isValidForSelectedPlan) {
      setValidPromoCode(promoCodeState.code)
    } else if (promoCodeState.isValid && !promoCodeState.isValidForSelectedPlan) {
      error = data.promo_code_wrong_plan_error ?? 'Invalid promo code for selected plan'
    } else if (!promoCodeState.isValid) {
      error = data.promo_code_error ?? 'Invalid promo code'
    }
    if (error) {
      setError('promoCode', { message: error })
      setValidPromoCode(undefined)
    } else {
      clearErrors()
    }
    fireAnalyticsEvent('BuyPlanPage_PromoCode_Submitted', {
      valid: promoCodeState.isValid,
      applied: promoCodeState.isValid && promoCodeState.isValidForSelectedPlan,
      promoCode: promoCodeState.code,
      selectedPlanType: selectedPlan?.type,
      selectedPlanInterval: selectedPlan?.interval
    })
    setIsSubmitted(false)
    setLastSelectedPlan(selectedPlan)
  }, [
    clearErrors,
    data.promo_code_error,
    data.promo_code_wrong_plan_error,
    isSubmitted,
    lastSelectedPlan,
    promoCodeState.code,
    promoCodeState.isLoading,
    promoCodeState.isValid,
    promoCodeState.isValidForSelectedPlan,
    selectedPlan,
    setError,
    setValidPromoCode
  ])
  const handlePromoCodeInput = (): void => {
    setIsSubmitted(true)
  }

  if (validPromoCode) {
    return null
  }
  return (
    <Box sx={{ paddingY: theme.spacing(8) }}>
      {showPromoCodeInput ? (
        <>
          <Stack spacing={2} direction="row">
            <TextField
              {...register('promoCode')}
              label={data.promo_code_input_label ?? 'Discount code'}
              fullWidth
              error={!!errors.promoCode}
              onBlur={e => {
                if (e.target.value !== lastValue) {
                  fireAnalyticsEvent('BuyPlanPage_PromoCodeInput_Changed', {
                    value: e.target.value
                  })
                }
                setLastValue(e.target.value)
              }}
              value={value}
              onChange={e => {
                setValue(e.target.value)
                clearErrors()
              }}
              onKeyDownCapture={e => {
                if (e.key === 'Enter') {
                  setLastValue(value)
                  e.preventDefault()
                  handlePromoCodeInput()
                }
              }}
            />
            <Button
              color="secondary"
              size="large"
              type="button"
              loading={isSubmitting}
              onClick={handleSubmit(handlePromoCodeInput)}
            >
              {data.promo_code_input_button_label}
            </Button>
          </Stack>
          <FormHelperText error={Boolean(errors.promoCode?.message)} role="alert">
            {errors.promoCode?.message}
          </FormHelperText>
        </>
      ) : (
        <Link
          component="button"
          onClick={() => {
            setShowPromoCodeInput(true)
            fireAnalyticsEvent('BuyPlanPage_PromoCodeSection_Opened')
          }}
          sx={{ color: 'onSurface.secondary' }}
          underline="always"
        >
          <Typography variant="bodySmall">{data.promo_code_dropdown_button}</Typography>
        </Link>
      )}
    </Box>
  )
}
